import { FC } from "react";
import { FieldArray, useFormikContext } from "formik";
import { FieldGroup } from "@blanket/components/layout/FieldGroup";
import FormSection from "@blanket/components/formSections/FormSection";
import SubGroupPanel from "@blanket/components/formSections/SubGroupPanel";
import Button from "@blanket/components/buttons/Button";
import TooltippedRemoveButton from "@blanket/components/buttons/TooltippedRemoveButton";
import type { CompanyRegistrationValues } from "../model/registrationTypes";
import { CompanyForm } from "../model/registrationEnums";
import { EmployeesFieldArray } from "../components/employees/EmployeesFieldArray";
import { optionalMobilnummerField } from "../components/employees/employeePhoneFieldConfig";
import { AdvokatselskabShareholderEligibilityFields } from "../components/eligibility/AdvokatselskabShareholderEligibilityFields";
import { emptyShareholder } from "../model/registrationInitialValues";
import {
  SelectFormField,
  TextFormField,
  YesNoFormField,
} from "@blanket/components/formFields/FormFields";
import { useCompanyNameLookup } from "@blanket/hooks/useCompanyNameLookup";
import { CompanyNameSuggestions } from "../components/CompanyNameSuggestions";
import { RegistrationDateDisclosureNotice } from "../components/RegistrationDateDisclosureNotice";
import { CompanyContactDetails } from "../components/CompanyContactDetails";
import { InvoiceDispatchFields } from "../components/InvoiceDispatchFields";
import { RegistrationRemarksField } from "../components/RegistrationRemarksField";
import { RegistrationContactFields } from "../components/RegistrationContactFields";

export const AdvokatselskabSection: FC = () => {
  const { values } = useFormikContext<CompanyRegistrationValues>();
  const advokatselskab = values.advokatselskab;
  const nameLookup = useCompanyNameLookup({
    name: "advokatselskab.name",
    cvr: "advokatselskab.cvr",
    pNumber: "advokatselskab.pNumber",
  });

  return (
    <>
      <FormSection
        id="section-advokatselskab-info"
        number={2}
        title="Stamoplysninger om selskabet"
        description="Stamoplysninger og forsikring."
      >
        <div className="d-flex flex-column gap-4">
          <CompanyNameSuggestions
            items={nameLookup.data}
            isLoading={nameLookup.isLoading}
            onSelect={nameLookup.select}
            onDismiss={nameLookup.dismiss}
          >
            <TextFormField
              name="advokatselskab.name"
              label="Selskabets navn"
              isRequired
              helperText='Er du opmærksom på kravene til navn, formål og branchekode i CVR? Et advokatselskab skal i navnet benytte den lovpligtige betegnelse afhængigt af selskabsformen, jf. retsplejelovens 124 a. Selskaber med drift skal have til formål at "udøve advokatvirksomhed" i CVR. Branchekoden skal være "691000: juridiske aktiviteter".'
              id="advokatselskab-input-name"
            />
          </CompanyNameSuggestions>
          <div className="blanket-two-col">
            <TextFormField
              name="advokatselskab.cvr"
              label="CVR-nr."
              id="advokatselskab-input-cvr"
              isRequired
              type="number"
            />
            {nameLookup.availablePNumbers.length > 1 ? (
              <SelectFormField
                key="advokatselskab-p-number-select"
                name="advokatselskab.pNumber"
                label="P-nummer"
                id="advokatselskab-input-p-number"
                isRequired
                placeholder="Vælg P-nummer"
                helperText="Vær opmærksom på, at der skal indsendes en blanket for hvert p-nummer, der oprettes. Det skyldes bl.a., at medarbejdere skal registreres under det korrekte p-nummer."
                options={nameLookup.availablePNumbers.map((p) => ({
                  value: String(p),
                  label: String(p),
                }))}
              />
            ) : (
              <TextFormField
                key="advokatselskab-p-number-text"
                name="advokatselskab.pNumber"
                label="P-nummer"
                id="advokatselskab-input-p-number"
                isRequired
                type="number"
                helperText="Vær opmærksom på, at der skal indsendes en blanket for hvert p-nummer, der oprettes. Det skyldes bl.a., at medarbejdere skal registreres under det korrekte p-nummer."
              />
            )}
          </div>
          <SelectFormField
            name="advokatselskab.companyForm"
            label="Selskabsform"
            id="advokatselskab-input-company-form"
            isRequired
            options={[
              { value: CompanyForm.AS, label: "A/S" },
              { value: CompanyForm.ApS, label: "ApS" },
              { value: CompanyForm.PS, label: "P/S" },
            ]}
          />
          <div className="d-flex flex-column gap-1">
            <div className="blanket-two-col">
              <TextFormField
                name="advokatselskab.insuranceCompany"
                label="Navn på forsikringsselskab"
                id="advokatselskab-input-insurance-company"
                type="text"
              />
              <TextFormField
                name="advokatselskab.insurancePolicyNumber"
                label="Policenummer"
                id="advokatselskab-input-insurance-policy-number"
                type="text"
              />
            </div>
            <div className="form-text mb-0">
              <p className="mb-2">
                Hvis du endnu ikke kender forsikringsselskabet og/eller
                policenummeret, skal dette eftersendes til Advokatsamfundet
                (forsikringer@advokatsamfundet.dk).
              </p>
              <p className="mb-0">
                Vær opmærksom på, at forsikringen skal være gældende fra
                opstartsdatoen.
              </p>
            </div>
          </div>
        </div>
      </FormSection>

      <FormSection
        id="section-advokatselskab-contact"
        number={3}
        title="Kontaktoplysninger til selskabet"
        description="Telefon, e-mail og fakturering."
      >
        <div className="d-flex flex-column gap-4">
          <CompanyContactDetails
            namePrefix="advokatselskab"
            idPrefix="advokatselskab"
          />
          <InvoiceDispatchFields
            namePrefix="advokatselskab"
            idPrefix="advokatselskab"
            invoiceTarget={advokatselskab.invoiceTarget}
          />
          <TextFormField
            name="advokatselskab.website"
            label="Virksomhedens hjemmeside"
            id="advokatselskab-input-website"
            type="url"
          />
        </div>
      </FormSection>

      <FormSection
        id="section-advokatselskab-participants"
        number={4}
        title="Oplysninger om deltagere"
        description="Selskabsdeltagere og driftsoplysninger."
      >
        <div className="d-flex flex-column gap-4">
          <SubGroupPanel
            className="blanket-participant-subsection"
            title="Selskabsdeltagere (tegnende / indtrædende)"
            description="Hvis en eller flere selskabsdeltagere er nyoprettede selskaber, skal der indsendes en blanket for hvert nyoprettet selskab."
          >
            <FieldArray name="advokatselskab.shareholders">
              {({ push, remove }) => (
                <FieldGroup className="blanket-participant-list">
                  {advokatselskab.shareholders.map((shareholder, index) => {
                    const namePrefix = `advokatselskab.shareholders.${index}`;
                    const idPrefix = `advokatselskab-sh-${index}`;

                    return (
                      <div
                        key={shareholder._rowId}
                        className="blanket-participant-card"
                      >
                        <div className="d-flex flex-column gap-4">
                          <div className="blanket-two-col">
                            <TextFormField
                              name={`${namePrefix}.name`}
                              label="Navn på selskabsdeltager"
                              id={`${idPrefix}-name`}
                              isRequired
                            />
                            <TextFormField
                              name={`${namePrefix}.cvrCpr`}
                              label="CVR / CPR"
                              id={`${idPrefix}-cvr`}
                              isRequired
                            />
                          </div>
                          <AdvokatselskabShareholderEligibilityFields
                            namePrefix={namePrefix}
                            idPrefix={idPrefix}
                            isLawyerOrLawFirm={shareholder.isLawyerOrLawFirm}
                            passedPartnerExam={shareholder.passedPartnerExam}
                            leavesOtherOnEntry={shareholder.leavesOtherOnEntry}
                          />
                        </div>
                        <div className="mt-3 d-flex justify-content-end">
                          <TooltippedRemoveButton
                            onClick={() => remove(index)}
                            ariaLabel="Fjern deltager"
                            disabled={advokatselskab.shareholders.length <= 1}
                            tooltipContent="Mindst én deltager skal angives"
                          >
                            Fjern
                          </TooltippedRemoveButton>
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    fullWidth={false}
                    onClick={() => push(emptyShareholder())}
                    aria-label="Tilføj selskabsdeltager"
                  >
                    Tilføj deltager
                  </Button>
                </FieldGroup>
              )}
            </FieldArray>
          </SubGroupPanel>
          <YesNoFormField
            name="advokatselskab.activePractice"
            label="Driver selskabet aktiv advokatvirksomhed?"
            isRequired
            helperText='Vær opmærksom på kravene til selskabets formål. Er der tale om et holdingselskab, skal formålet i CVR være at "eje aktier eller anparter i et andet advokatselskab".'
            id="advokatselskab-input-active-practice"
          />
          <EmployeesFieldArray
            namePrefix="advokatselskab"
            phoneField={optionalMobilnummerField}
            employeesQuestionLabel="Er der ansatte i selskabet?"
          />
        </div>
      </FormSection>

      <FormSection
        id="section-advokatselskab-dates"
        number={5}
        title="Oprettelses- og driftdato"
      >
        <div className="mb-4">
          <RegistrationRemarksField
            namePrefix="advokatselskab"
            idPrefix="advokatselskab"
          />
        </div>
        <div className="blanket-two-col">
          <TextFormField
            name="advokatselskab.cvrCreationDate"
            label="Oprettelsesdato i CVR"
            isRequired
            helperText="Hvis oprettelsesdato og dato for opstart af drift er den samme, indtastes dette."
            id="advokatselskab-input-cvr-creation-date"
            type="date"
          />
          <TextFormField
            name="advokatselskab.startDate"
            label="Dato for opstart af drift"
            isRequired
            helperText="Hvis oprettelsesdato og dato for opstart af drift er den samme, indtastes dette."
            id="advokatselskab-input-start-date"
            type="date"
          />
        </div>
        <RegistrationDateDisclosureNotice />
        <div className="blanket-two-col mt-4">
          <RegistrationContactFields
            namePrefix="advokatselskab"
            idPrefix="advokatselskab"
          />
        </div>
      </FormSection>
    </>
  );
};
